"use client";

import { useState } from "react";
import { Eye, Copy, Check } from "lucide-react";
import { useUser } from "@/app/context/UserContext";
import Accordion from "../Elements/Accordion";
import { Constantes } from "../../constants/constantsGlobal";

const PREFIX = "historic_conversations_from_";

function isValidViewName(value: string) {
  return /^[a-zA-Z0-9_]*$/.test(value);
}

export default function ViewIdTab() {
  const { userProject, setUserProject } = useUser();
  const project = userProject?.project;

  const viewName = project?.view_name ?? null;
  const viewId = project?.view_id ?? null;

  const [open, setOpen] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);

  const isEnabled =
    project?.view_name !== null && project?.view_name !== undefined;
  const isLocked = Boolean(viewId);

  const handleEnable = () => {
    setUserProject((prev: any) => {
      const hasAlias =
        typeof prev.project.alias === "string" &&
        prev.project.alias.trim().length > 0;

      return {
        ...prev,
        project: {
          ...prev.project,
          alias_auto: !hasAlias,
          view_name:
            prev.project.view_name ??
            (hasAlias ? prev.project.alias : ""),
        },
      };
    });
  };

  const handleChangeViewName = (nextValue: string) => {
    if (isLocked) return;
    if (!isValidViewName(nextValue)) return;

    setUserProject((prev: any) => ({
      ...prev,
      project: {
        ...prev.project,
        view_name: nextValue,
        alias: prev.project.alias_auto
          ? nextValue
          : prev.project.alias,
      },
    }));
  };

  const handleCopy = async () => {
    if (!viewId) return;
    await navigator.clipboard.writeText(viewId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleDownload = async () => {
    if (!startDate || !endDate || !project?.id) return;

    try {
      setLoading(true);

      const response = await fetch(Constantes.URI_ChatbotDownloadConversation, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          start_date: startDate,
          end_date: endDate,
          project_id: project.id,
        }),
      });

      if (!response.ok) {
        throw new Error("Error al generar el CSV");
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "conversation.csv";
      document.body.appendChild(a);
      a.click();

      a.remove();
      window.URL.revokeObjectURL(url);

      setShowModal(false);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3 p-8">
      <Accordion
        title="Vista histórica del proyecto"
        index={0}
        isOpen={open}
        onClick={() => setOpen((v) => !v)}
        icon={<Eye className="w-4 h-4" />}
        className="border border-gray-200 rounded-2xl overflow-hidden n-elevation-1"
        classNameHead="w-full flex justify-between items-center bg-gray-100 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-200 transition-all"
      >
        <div className="p-8 space-y-6">
          {!isEnabled ? (
            <>
              <p className="text-sm text-gray-600">
                Habilita el histórico del proyecto creando una vista en BigQuery.
              </p>

              <button
                onClick={handleEnable}
                className="rounded-xl bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 transition"
              >
                Habilitar histórico
              </button>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Nombre de la vista (BigQuery)
                </label>

                {/* Input con prefijo visual */}
                <div className="flex items-center rounded-xl border px-4 py-2 font-mono text-sm">
                  <span className="text-gray-400 select-none">
                    {PREFIX}
                  </span>

                  <input
                    type="text"
                    value={viewName ?? ""}
                    onChange={(e) => handleChangeViewName(e.target.value)}
                    readOnly={isLocked}
                    className={`flex-1 outline-none ml-1 ${
                      isLocked ? "bg-transparent text-gray-700" : ""
                    }`}
                  />
                </div>

                <p className="text-xs text-gray-500">
                  Solo se permiten letras, números y guiones bajos (<code>_</code>).<br />
                  Si el proyecto ya tenía alias, se usa como valor inicial.<br />
                  Este nombre no será editable una vez creada la vista en BigQuery.
                </p>
              </div>

              {viewId && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    BigQuery View ID
                  </label>

                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={viewId}
                      readOnly
                      className="flex-1 rounded-xl border border-gray-300 bg-gray-50 px-4 py-2 text-sm font-mono text-gray-700"
                    />

                    <button
                      onClick={handleCopy}
                      className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 transition"
                    >
                      {copied ? (
                        <>
                          <Check className="w-4 h-4 text-green-600" />
                          Copiado
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          Copiar
                        </>
                      )}
                    </button>
                  </div>
                  <button
                    onClick={() => setShowModal(true)}
                    className="mt-4 rounded-xl bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 transition"
                  >
                    Descargar conversaciones
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </Accordion>
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl space-y-4">
            <h2 className="text-lg font-semibold">
              Descargar histórico
            </h2>

            <div className="space-y-2">
              <label className="text-sm font-medium">Fecha inicio</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full rounded-xl border px-3 py-2"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Fecha fin</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full rounded-xl border px-3 py-2"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                onClick={() => setShowModal(false)}
                className="rounded-xl border px-4 py-2 text-sm"
              >
                Cancelar
              </button>

              <button
                onClick={handleDownload}
                disabled={loading}
                className="rounded-xl bg-black px-4 py-2 text-sm text-white hover:bg-gray-800 disabled:opacity-50"
              >
                {loading ? "Generando..." : "Descargar CSV"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
